// Gráficas de Finanzas hechas con cajas de Chakra (sin librería de gráficas).
import { useState } from 'react';
import { Box, Flex, Grid, Text, Tooltip } from '@chakra-ui/react';
import { IN_COLOR, OUT_COLOR, formatMillions, formatMoney } from 'api/administrador/finance';
import { Card, SegmentedButtons } from './ui';

const OTHER_COLOR = '#a0aec0';

function Legend() {
  return (
    <Flex gap={4} fontSize="13px" color="gray.600" align="center">
      <Flex gap={1.5} align="center"><Box w="10px" h="10px" borderRadius="2px" bg={IN_COLOR} />Ingresos</Flex>
      <Flex gap={1.5} align="center"><Box w="10px" h="10px" borderRadius="2px" bg={OUT_COLOR} />Egresos</Flex>
    </Flex>
  );
}

/**
 * Barras horizontales por categoría con selector Egresos / Ingresos (empieza en egresos).
 * `data` = { egreso: [{ name, total, uncategorized }], ingreso: [...] }. Muestra 7 y agrupa el resto.
 */
export function CategoryBars({ title, subtitle, data, words = ['Egresos', 'Ingresos'], unit = 'millions', limit = 7 }) {
  const [kind, setKind] = useState('egreso');
  const list = (data?.[kind] || []).filter((x) => x.total > 0);
  const top = list.slice(0, limit);
  const rest = list.slice(limit).reduce((a, x) => a + x.total, 0);
  const rows = rest > 0 ? [...top, { name: 'Otras categorías', total: rest, other: true }] : top;
  const max = Math.max(1, ...rows.map((r) => r.total));
  const color = kind === 'egreso' ? OUT_COLOR : IN_COLOR;
  const fmt = (v) => (unit === 'millions' ? formatMillions(v) : formatMoney(v));

  return (
    <Card
      title={title || `${kind === 'egreso' ? words[0] : words[1]} por categoría`}
      description={subtitle}
      actions={
        <SegmentedButtons
          size="xs"
          ariaLabel="Ver por categoría"
          value={kind}
          onChange={setKind}
          options={[{ value: 'egreso', label: words[0] }, { value: 'ingreso', label: words[1] }]}
        />
      }
    >
      {rows.length === 0 ? (
        <Text fontSize="sm" color="gray.500">No hay {kind === 'egreso' ? words[0].toLowerCase() : words[1].toLowerCase()} en este periodo.</Text>
      ) : (
        <Flex direction="column" gap={2}>
          {rows.map((r) => (
            <Grid key={r.name} templateColumns="170px 1fr 72px" gap={2.5} alignItems="center" fontSize="13px">
              <Text color="gray.600" noOfLines={1} title={r.name}>{r.name}</Text>
              <Box h="14px" bg="gray.100" borderRightRadius="4px">
                <Box h="14px" w={`${(r.total / max) * 100}%`} minW="2px" borderRightRadius="4px" bg={r.other || r.uncategorized ? OTHER_COLOR : color} />
              </Box>
              <Text textAlign="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(r.total)}</Text>
            </Grid>
          ))}
        </Flex>
      )}
    </Card>
  );
}

/** Barras verticales de ingresos y egresos por periodo (semanas o meses). `items` = [{ label, in, out, foot }]. */
export function PairBars({ title, subtitle, items, height = 170 }) {
  const max = Math.max(1, ...items.flatMap((i) => [i.in, i.out]));
  return (
    <Card title={title} description={subtitle} actions={<Legend />}>
      <Flex align="flex-end" gap={4} h={`${height + 24}px`} borderBottom="1px solid" borderColor="gray.300" px={2} role="img" aria-label={title}>
        {items.map((i) => (
          <Flex key={i.label} flex={1} justify="center" align="flex-end" gap={1} h="100%">
            {[['in', IN_COLOR, 'Ingresos'], ['out', OUT_COLOR, 'Egresos']].map(([key, color, name]) => (
              <Flex key={key} direction="column" align="center" gap={1}>
                <Text fontSize="11px" color="gray.600" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatMillions(i[key])}</Text>
                <Tooltip label={`${name} ${i.label}: ${formatMoney(i[key])}`} hasArrow>
                  <Box w="22px" h={`${Math.round((i[key] / max) * height)}px`} minH={i[key] ? '2px' : 0} bg={color} borderTopRadius="4px" />
                </Tooltip>
              </Flex>
            ))}
          </Flex>
        ))}
      </Flex>
      <Flex gap={4} px={2} mt={1}>
        {items.map((i) => (
          <Box key={i.label} flex={1} textAlign="center" fontSize="12px" color="gray.500">
            {i.label}
            {i.foot && <Text color="gray.600" sx={{ fontVariantNumeric: 'tabular-nums' }}>{i.foot}</Text>}
          </Box>
        ))}
      </Flex>
    </Card>
  );
}

/** Saldo al cierre de cada día con la línea del máximo de la caja. `points` = [{ day, balance }]. */
export function DailyBalance({ points, maxBalance, height = 160 }) {
  const top = Math.max(1, maxBalance || 0, ...points.map((p) => p.balance));
  return (
    <Card title="Saldo al cierre de cada día" description={maxBalance ? `La línea punteada es el máximo de ${formatMoney(maxBalance)}` : undefined}>
      {points.length === 0 ? (
        <Text fontSize="sm" color="gray.500">Configura la caja inicial para ver el saldo.</Text>
      ) : (
        <>
          <Flex position="relative" align="flex-end" gap={1.5} h={`${height}px`} borderBottom="1px solid" borderColor="gray.300" px={1} role="img" aria-label="Saldo diario de la caja">
            {points.map((p) => (
              <Tooltip key={p.day} label={`${p.day === 0 ? 'Caja inicial' : `Día ${p.day}`}: ${formatMoney(p.balance)}`} hasArrow>
                <Box flex={1} bg={IN_COLOR} borderTopRadius="4px" h={`${Math.max(0, Math.round((p.balance / top) * (height - 8)))}px`} />
              </Tooltip>
            ))}
            {maxBalance ? (
              <>
                <Box position="absolute" left={0} right={0} bottom={`${Math.round((maxBalance / top) * (height - 8))}px`} borderTop="2px dashed" borderColor="red.500" />
                <Text position="absolute" right={1} bottom={`${Math.round((maxBalance / top) * (height - 8)) + 4}px`} fontSize="11px" fontWeight="600" color="red.600" bg="white" px={1}>
                  Máx. {formatMoney(maxBalance)}
                </Text>
              </>
            ) : null}
          </Flex>
          <Flex gap={1.5} px={1} mt={1}>
            {points.map((p) => (
              <Text key={p.day} flex={1} textAlign="center" fontSize="11px" color="gray.500">{p.day === 0 ? 'Ini.' : p.day}</Text>
            ))}
          </Flex>
        </>
      )}
    </Card>
  );
}
